import React, { useEffect, useMemo, useState } from 'react';

import { libelle, nomLisible } from '../../../lib/libelles';
import { formatLitres } from '../../../lib/formats';
import { MODES_PROPRES, NATURE_REAPPRO, NATURE_VENTE, supplyApi } from '../../../lib/supplyApi';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { SearchableSelect } from '../../ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

/**
 * Nature de la commande (lot 032) : « Vente » ou « Réapprovisionnement de ma
 * station », puis la station et ses cuves.
 *
 * Rien ne s'affiche pour une société sans station : le formulaire de vente
 * reste celui d'avant. Une station revendeur se choisit seulement sur une
 * vente à son client ; une station de la société, seulement en
 * réapprovisionnement (pas de client, pas de prix, pas de facture client).
 *
 *  - `valeur`   : `{ supply_kind, destination_station_id, destination_tanks }` ;
 *  - `onChange` : reçoit les champs modifiés ;
 *  - `clientId` : client choisi (stations revendeur de ce client) ;
 *  - `actif`    : charge la liste à l'ouverture du formulaire.
 */

/** Toutes les stations livrables, chargées une fois par ouverture du formulaire. */
export const useStationsLivrables = (actif = true) => {
  const [stations, setStations] = useState([]);
  const [chargement, setChargement] = useState(false);
  useEffect(() => {
    if (!actif) return undefined;
    let annule = false;
    setChargement(true);
    supplyApi
      .stations()
      .then((liste) => {
        if (!annule) setStations(Array.isArray(liste) ? liste : []);
      })
      .catch(() => {
        // Sans la liste (hors ligne, droit absent), la commande reste une vente.
        if (!annule) setStations([]);
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, [actif]);
  return { stations, chargement };
};

const optionStation = (station) => ({
  value: station.id,
  label: nomLisible(station.name, station.code),
  description: [libelle('station_mode', station.operating_mode), station.city].filter(Boolean).join(' · ') || undefined,
  keywords: [station.code, station.city].filter(Boolean),
});

export const OrderSupplyFields = ({ valeur, onChange, clientId, actif = true, verrouille = false }) => {
  const { stations } = useStationsLivrables(actif);
  const nature = valeur?.supply_kind || NATURE_VENTE;
  const stationId = valeur?.destination_station_id || '';
  const cuves = useMemo(() => (Array.isArray(valeur?.destination_tanks) ? valeur.destination_tanks : []), [valeur]);

  const propres = useMemo(() => stations.filter((station) => MODES_PROPRES.includes(station.operating_mode)), [stations]);
  const revendeurs = useMemo(
    () => stations.filter((station) => station.operating_mode === 'DEALER' && (!clientId || station.client_id === clientId)),
    [stations, clientId],
  );
  const choix = nature === NATURE_REAPPRO ? propres : revendeurs;
  const station = choix.find((item) => item.id === stationId) || null;
  const options = useMemo(() => choix.map(optionStation), [choix]);

  // Aucune station : la commande reste une vente, sans champ de plus.
  if (!propres.length && !revendeurs.length && nature !== NATURE_REAPPRO) return null;

  const changerNature = (code) =>
    onChange({ supply_kind: code, destination_station_id: '', destination_tanks: [] });

  const choisirStation = (id) => {
    const suivante = choix.find((item) => item.id === id);
    // Une seule cuve : elle est cochée d'office.
    const uniques = suivante?.tanks?.length === 1 ? [{ tank_id: suivante.tanks[0].id, quantity_liters: null }] : [];
    onChange({ destination_station_id: id, destination_tanks: uniques });
  };

  const cocher = (tankId, coche) => {
    const reste = cuves.filter((item) => item.tank_id !== tankId);
    onChange({ destination_tanks: coche ? [...reste, { tank_id: tankId, quantity_liters: null }] : reste });
  };

  const litres = (tankId, texte) => {
    const nombre = texte === '' ? null : Number(String(texte).replace(',', '.'));
    onChange({
      destination_tanks: cuves.map((item) =>
        item.tank_id === tankId ? { ...item, quantity_liters: Number.isFinite(nombre) && nombre > 0 ? nombre : null } : item,
      ),
    });
  };

  return (
    <div className="space-y-3" data-testid="commande-nature">
      {propres.length ? (
        <div className="space-y-1">
          <Label>Nature</Label>
          <Select value={nature} onValueChange={changerNature} disabled={verrouille}>
            <SelectTrigger aria-label="Nature de la commande">
              <SelectValue placeholder="Nature de la commande" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NATURE_VENTE}>{libelle('commande_nature', NATURE_VENTE)}</SelectItem>
              <SelectItem value={NATURE_REAPPRO}>{libelle('commande_nature', NATURE_REAPPRO)}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {choix.length ? (
        <div className="space-y-1">
          <Label>{nature === NATURE_REAPPRO ? 'Station à réapprovisionner' : 'Station du client'}</Label>
          <SearchableSelect
            value={stationId}
            onValueChange={choisirStation}
            options={options}
            placeholder="Choisir la station"
            searchPlaceholder="Rechercher une station…"
            emptyMessage="Aucune station"
            disabled={verrouille}
          />
        </div>
      ) : null}

      {station ? (
        <div className="space-y-1">
          <Label>Cuves</Label>
          {station.tanks?.length ? (
            <ul className="divide-y divide-border/60 rounded-lg border border-border">
              {station.tanks.map((cuve) => {
                const ligne = cuves.find((item) => item.tank_id === cuve.id);
                const id = `cuve-${cuve.id}`;
                return (
                  <li key={cuve.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                    <Checkbox id={id} checked={Boolean(ligne)} onCheckedChange={(coche) => cocher(cuve.id, Boolean(coche))} />
                    <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                      <span className="font-medium text-foreground">{cuve.name}</span>
                      <span className="text-muted-foreground"> · {cuve.product_name || '—'}</span>
                      {cuve.ullage_liters != null ? (
                        <span className="block text-xs text-muted-foreground">Place libre {formatLitres(cuve.ullage_liters)}</span>
                      ) : null}
                    </label>
                    {ligne ? (
                      <Input
                        className="h-8 w-32 text-right tabular-nums"
                        inputMode="decimal"
                        aria-label={`Litres pour ${cuve.name}`}
                        placeholder="Litres"
                        value={ligne.quantity_liters ?? ''}
                        onChange={(event) => litres(cuve.id, event.target.value)}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune cuve active</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Champs à envoyer au serveur pour la nature choisie : rien pour une vente sans
 * station (la vente reste exactement celle d'avant). `natureAvant` : nature de
 * la commande modifiée ; un réapprovisionnement qui redevient une vente envoie
 * une station vide pour l'effacer.
 */
export const champsNature = (valeur, natureAvant) => {
  const nature = valeur?.supply_kind || NATURE_VENTE;
  const stationId = valeur?.destination_station_id || '';
  if (nature !== NATURE_REAPPRO && !stationId && natureAvant !== NATURE_REAPPRO) return {};
  return {
    supply_kind: nature,
    destination_station_id: stationId,
    destination_tanks: (valeur?.destination_tanks || []).map((item) => ({
      tank_id: item.tank_id,
      quantity_liters: item.quantity_liters ?? null,
    })),
  };
};

export default OrderSupplyFields;
